import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

/**
 * VideoLightbox — supports YouTube/Vimeo iframe embeds AND local mp4 files.
 * Detect by extension: ends with .mp4/.webm → <video>, otherwise iframe.
 */
function isLocalVideo(url) {
  if (!url) return false;
  return /\.(mp4|webm|mov)(\?|$)/i.test(url);
}

export default function VideoLightbox({ open, onClose, videoUrl, title }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) {
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const local = isLocalVideo(videoUrl);

  return (
    <AnimatePresence>
      {open && videoUrl && (
        <motion.div
          data-testid="video-lightbox"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[90] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl aspect-video bg-black border border-hair"
            onClick={(e) => e.stopPropagation()}
          >
            {local ? (
              <video
                src={videoUrl}
                autoPlay
                controls
                playsInline
                className="w-full h-full object-contain bg-black"
              />
            ) : (
              <iframe
                src={`${videoUrl}?autoplay=1&rel=0`}
                title={title || "video"}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </motion.div>
          <button
            onClick={onClose}
            aria-label="close"
            data-testid="lightbox-close"
            className="absolute top-4 sm:top-6 right-4 sm:right-6 w-11 h-11 sm:w-12 sm:h-12 border border-hair hover:border-ink-100 text-ink-100 flex items-center justify-center bg-black/50 backdrop-blur"
          ><X size={18} /></button>
          {title && <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 px-3 text-ink-300 text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-center">{title}</div>}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
